<script setup lang="ts">
import type { EventVault } from "@/models"
import type { ISegmentedControl } from "@/types"
import { computed } from "vue"
import { Alert, Badge, Button, ButtonLink, Icon, Popover, SegmentedControl } from "@/components/ui"
import { useTranslation } from "@/composables"
import { GalleryMode } from "@/types"

interface GalleryActionsProps {
  eventVault?: EventVault
  isHost?: boolean
  displayUpload?: boolean
  isLoading?: boolean
  isLoadingEvent?: boolean
  galleryMode?: GalleryMode
  pendingMediaCount?: number
  activeFiltersCount?: number
}

withDefaults(defineProps<GalleryActionsProps>(), {
  eventVault: undefined,
  isHost: false,
  displayUpload: false,
  isLoading: false,
  isLoadingEvent: false,
  galleryMode: GalleryMode.grid,
  pendingMediaCount: undefined,
  activeFiltersCount: undefined,
})

const emit = defineEmits<{
  upload: []
  refresh: []
  filter: []
  changeMode: [mode: GalleryMode]
}>()

const { t } = useTranslation()

const helpLinkByBrand: Record<string, string> = {
  guestpix: "https://help.guestpix.com/category/167-uploading-downloading",
  honorpix: "https://help.honorpix.com/category/204-uploads-and-downloads",
}

function getHelpLink(): string {
  const brand = import.meta.env.VITE_BRAND || "guestpix"
  return helpLinkByBrand[brand] ?? helpLinkByBrand.guestpix
}

const helpLink = getHelpLink()

const galleryModeOptions = computed<ISegmentedControl[]>(() => [
  {
    value: GalleryMode.grid,
    label: t("grid"),
    icon: "Grid",
    dataTest: "grid_view_button",
  },
  {
    value: GalleryMode.feed,
    label: t("feed"),
    icon: "Cards",
    dataTest: "feed_view_button",
  },
])
</script>

<template>
  <div v-if="!isLoading && !displayUpload" class="mt-4 md:hidden">
    <Alert variant="info" :closable="false" prefix-icon="CircleInfo" data-test="uploads_disabled_alert">
      <strong>{{ t("gallery_page.uploads_disabled_message1") }}</strong>
      <br>
      {{ t("gallery_page.uploads_disabled_message2") }}<br>
    </Alert>
  </div>

  <div class="sticky top-0 z-40 -mx-px bg-white px-px py-4">
    <div class="flex gap-2 justify-between items-start">
      <div v-if="displayUpload" class="flex gap-2">
        <Button v-if="!isLoadingEvent" prefix-icon="ImageUpload" data-test="upload_button" @click="() => emit('upload')">
          <span class="hidden md:hidden lg:inline">
            {{ eventVault?.eventVaultSettings?.videoEnabled ? t("upload_photos_and_videos") : t("upload_photos") }}
          </span>
          <span class="md:inline lg:hidden">{{ t("upload") }}</span>
        </Button>
        <div
          v-if="pendingMediaCount && (!eventVault?.eventVaultSettings?.disableGuestUpload || isHost)"
          class="hidden md:flex gap-2"
        >
          <Button variant="icon" prefix-icon="Redo" @click="() => emit('refresh')">
            {{ pendingMediaCount }}
            <span class="sr-only">{{ t("gallery_page.processing_sr") }}</span>
          </Button>
          <div class="hidden md:flex">
            <Popover :content="t('gallery_page.processing_tooltip')" hover>
              <Icon icon="CircleInfo" class="hidden md:flex cursor-pointer" />
            </Popover>
          </div>
        </div>
      </div>
      <div v-if="!isLoading && !displayUpload" class="hidden md:inline">
        <Alert variant="info" :closable="false" prefix-icon="CircleInfo">
          <strong>{{ t("gallery_page.uploads_disabled_message1") }}</strong>
          <br>
          {{ t("gallery_page.uploads_disabled_message2") }}<br>
        </Alert>
      </div>
      <div class="flex gap-2">
        <div>
          <SegmentedControl
            variant="primary"
            :model-value="galleryMode"
            :options="galleryModeOptions"
            hide-labels-below-md
            no-padding
            with-border
            @update:model-value="(mode) => emit('changeMode', mode as GalleryMode)"
          />
        </div>
        <Badge :count="activeFiltersCount ?? 0">
          <Button prefix-icon="Filter" variant="tertiary" @click="() => emit('filter')">
            <span class="hidden lg:flex">{{ t("filter") }}</span>
          </Button>
        </Badge>
        <div class="hidden md:flex">
          <ButtonLink
            v-if="displayUpload"
            variant="flat"
            :href="helpLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t("help") }}
          </ButtonLink>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="pendingMediaCount && (!eventVault?.eventVaultSettings?.disableGuestUpload || isHost)"
    class="flex gap-2 md:hidden mb-4"
  >
    <Button variant="icon" prefix-icon="Redo" @click="() => emit('refresh')">
      {{ pendingMediaCount }}
      <span class="sr-only">{{ t("gallery_page.processing_sr") }}</span>
    </Button>
    <Popover :content="t('gallery_page.processing_tooltip')" hover>
      <Icon icon="CircleInfo" class="cursor-pointer" />
    </Popover>
  </div>
</template>
